import { parseTopics } from '../topics/topic.utils';
import type { CreatorSiteSectionInput } from './creator-site-sections';
import { CREATOR_SITE_TEXT_LONG_MAX } from './creator-site-sections';

export type CreatorSiteProfileSeedSource = {
  username: string;
  displayName?: string | null;
  headline?: string | null;
  bio?: string | null;
  websiteUrl?: string | null;
  topics?: unknown;
  socialLinkCount: number;
};

export type CreatorSiteProfileSeedResult = {
  siteTitle: string;
  seoTitle: string;
  seoDescription: string | null;
  sections: CreatorSiteSectionInput[];
};

function clip(value: string, max: number) {
  return value.length <= max ? value : `${value.slice(0, max - 1).trim()}…`;
}

function upsertSection(
  sections: CreatorSiteSectionInput[],
  type: CreatorSiteSectionInput['type'],
  content: Record<string, unknown>,
  options?: { insertAfterType?: string },
) {
  const index = sections.findIndex((section) => section.type === type);
  if (index >= 0) {
    return sections.map((section, sectionIndex) =>
      sectionIndex === index
        ? { ...section, content: { ...(section.content as Record<string, unknown>), ...content } }
        : section,
    );
  }

  const newSection: CreatorSiteSectionInput = {
    type,
    variant: 'default',
    visible: true,
    content,
  };

  if (options?.insertAfterType) {
    const afterIndex = sections.findIndex((section) => section.type === options.insertAfterType);
    if (afterIndex >= 0) {
      return [
        ...sections.slice(0, afterIndex + 1),
        newSection,
        ...sections.slice(afterIndex + 1),
      ];
    }
  }

  return [...sections, newSection];
}

function ensureLayoutSections(sections: CreatorSiteSectionInput[]) {
  let next = sections;
  if (!next.some((section) => section.type === 'navbar')) {
    next = [{ type: 'navbar', variant: 'default', visible: true, content: {} }, ...next];
  }
  if (!next.some((section) => section.type === 'footer')) {
    next = [...next, { type: 'footer', variant: 'default', visible: true, content: {} }];
  }
  return next;
}

export function buildCreatorSiteProfileSeed(
  profile: CreatorSiteProfileSeedSource,
  existingSections: CreatorSiteSectionInput[],
): CreatorSiteProfileSeedResult {
  const displayName = profile.displayName?.trim() || profile.username;
  const headline = profile.headline?.trim() || '';
  const bio = profile.bio?.trim() || '';
  const topics = parseTopics(profile.topics);
  const aboutBody = clip(
    [bio, topics.length > 0 ? `Topics: ${topics.join(', ')}` : ''].filter(Boolean).join('\n\n') ||
      'Tell your story here.',
    CREATOR_SITE_TEXT_LONG_MAX,
  );

  const existing = existingSections.map((section) => ({
    id: section.id,
    type: section.type,
    variant: section.variant ?? 'default',
    visible: section.visible ?? true,
    content: (section.content as Record<string, unknown>) ?? {},
    bindings: section.bindings,
  }));

  let sections = ensureLayoutSections(existing);
  sections = upsertSection(sections, 'hero', {
    headline: displayName,
    subheadline: headline,
  }, { insertAfterType: 'navbar' });
  sections = upsertSection(sections, 'about', {
    headline: 'About',
    body: aboutBody,
  }, { insertAfterType: 'hero' });

  if (profile.socialLinkCount > 0) {
    sections = upsertSection(sections, 'social_links', {}, { insertAfterType: 'about' });
  }

  const websiteUrl = profile.websiteUrl?.trim();
  if (websiteUrl) {
    sections = upsertSection(
      sections,
      'link_row',
      {
        links: [{ label: 'Website', href: websiteUrl }],
      },
      { insertAfterType: profile.socialLinkCount > 0 ? 'social_links' : 'about' },
    );
  }

  return {
    siteTitle: displayName,
    seoTitle: displayName,
    seoDescription: headline || bio || null,
    sections,
  };
}
